/**
 * 盤面の集約
 *
 * 環状40マス（国28＋特殊12）を id 順に並べる。
 * ルール側はこの配列だけを見る。docs/01-ゲームデザイン.md §2.2
 */

import type { Province, Square, SquareId } from './types';

import { KAI, OWARI } from './provinces/_reference';
import { ISE, OMI, TANBA, WAKASA } from './provinces/kinai';
import { MIKAWA, SURUGA, TOTOMI } from './provinces/tokai';
import { HITACHI, MUSASHI, SAGAMI, SHIMOUSA } from './provinces/kanto';
import { DEWA, ECHIGO, MUTSU } from './provinces/tohoku';
import { ECCHU, KAGA, SHINANO } from './provinces/chubu';
import { IZUMO, IWAMI, NAGATO, TAJIMA } from './provinces/sanin';
import { BUNGO, CHIKUZEN, HIZEN, IYO, SATSUMA } from './provinces/saigoku';
import { SPECIAL_EAST_SQUARES } from './squares/special-east';
import { SPECIAL_WEST_SQUARES } from './squares/special-west';

/** 国マス 28。id 順ではない。BOARD が並べ替える */
export const PROVINCES: readonly Province[] = [
  OMI, ISE, OWARI, MIKAWA, TOTOMI, SURUGA, SAGAMI, MUSASHI,
  SHIMOUSA, HITACHI, MUTSU, DEWA, ECHIGO, SHINANO, KAI, ECCHU,
  KAGA, WAKASA, TANBA, TAJIMA, IZUMO, IWAMI, NAGATO, CHIKUZEN,
  HIZEN, SATSUMA, BUNGO, IYO,
];

/** 特殊マス 12（京・宿場・湊・関所・寺社・合戦・災害・評定） */
export const SPECIAL_SQUARES: readonly Square[] = [
  ...SPECIAL_EAST_SQUARES,
  ...SPECIAL_WEST_SQUARES,
];

/** 環状40マス。添字がそのまま SquareId になる */
export const BOARD: readonly Square[] = [...PROVINCES, ...SPECIAL_SQUARES].sort(
  (a, b) => a.id - b.id,
);

export const BOARD_SIZE = 40;

export const squareAt = (id: SquareId): Square => {
  const square = BOARD[id];
  if (!square) throw new RangeError(`no square at id ${id}`);
  return square;
};

export const isProvince = (square: Square): square is Province => square.kind === 'province';

/** 環状なので、前方へ n マス進んだ先を返す */
export const advance = (from: SquareId, n: number): SquareId =>
  (((from + n) % BOARD_SIZE) + BOARD_SIZE) % BOARD_SIZE;

/**
 * 京（id 0）までの前方距離。
 * 盤は一方通行の環なので、直線距離ではなく「あと何マス進めば京か」で測る。
 * 武将の開始国の有利不利を評価するときは必ずこちらを使う（docs/01 §8）。
 */
export const distanceToKyoto = (from: SquareId): number => (BOARD_SIZE - from) % BOARD_SIZE;
